import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["selectAll", "member", "count", "submitBtn"]
  static values = {
    url: String,
    meetingKind: String
  }

  connect() {
    this.refreshState()

    this._onModalOpen = (e) => {
      const targetId = e.detail?.id
      const triggerId = e.target?.closest?.('[data-modal-open]')?.dataset?.modalOpen
      const myId = this.element.dataset.modalIdValue
      if (targetId === myId || triggerId === myId) this._resetSelection()
    }
    document.addEventListener("modal:open-by-id", this._onModalOpen)
    document.addEventListener("click", this._onModalOpen)
  }

  disconnect() {
    document.removeEventListener("modal:open-by-id", this._onModalOpen)
    document.removeEventListener("click", this._onModalOpen)
  }

  _resetSelection() {
    this.memberTargets.forEach((cb) => { cb.checked = true })
    this.refreshState()
  }

  toggleAll(event) {
    const checked = event.target.checked
    this.memberTargets.forEach((cb) => { cb.checked = checked })
    this.refreshState()
  }

  refreshState() {
    const total = this.memberTargets.length
    const selected = this.memberTargets.filter((cb) => cb.checked).length

    if (this.hasCountTarget) this.countTarget.textContent = selected

    if (this.hasSelectAllTarget) {
      this.selectAllTarget.checked = selected === total && total > 0
      this.selectAllTarget.indeterminate = selected > 0 && selected < total
    }

    if (this.hasSubmitBtnTarget) {
      this.submitBtnTarget.disabled = selected === 0
    }
  }

  async submit(event) {
    if (event) event.preventDefault()

    const userIds = this.memberTargets
      .filter((cb) => cb.checked)
      .map((cb) => cb.value)

    if (userIds.length === 0) return

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

    this.submitBtnTarget.disabled = true
    const originalText = this.submitBtnTarget.textContent
    this.submitBtnTarget.textContent = "Sending..."

    try {
      const formData = new FormData()
      formData.append("meeting_kind", this.meetingKindValue)
      userIds.forEach((id) => formData.append("user_ids[]", id))

      const response = await fetch(this.urlValue, {
        method: "POST",
        headers: { "Accept": "application/json", "X-CSRF-Token": csrfToken },
        body: formData
      })

      if (!response.ok) {
        const data = await response.json().catch(() => ({}))
        throw new Error(data.error || `HTTP ${response.status}`)
      }

      const data = await response.json()
      const count = data.count ?? userIds.length
      this._showToast(`Calendar invite resent to ${count} ${count === 1 ? "recipient" : "recipients"}.`, "notice")
      this._close()
    } catch (err) {
      this._showToast(err.message || "Could not resend the calendar invite. Please try again.", "alert")
    } finally {
      this.submitBtnTarget.disabled = false
      this.submitBtnTarget.textContent = originalText
      this.refreshState()
    }
  }

  _close() {
    this.element.classList.add("hidden")
    document.body.style.overflow = ""
  }

  _showToast(message, type = "notice") {
    const container = document.createElement("div")
    container.className = type === "alert" ? "flash-toast-alert" : "flash-toast-notice"
    container.dataset.controller = "flash"
    container.dataset.flashDismissAfterValue = "5000"
    container.setAttribute("role", "alert")

    const iconDiv = document.createElement("div")
    iconDiv.className = "shrink-0"
    iconDiv.innerHTML = type === "alert"
      ? `<svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" /></svg>`
      : `<svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>`

    const msgEl = document.createElement("p")
    msgEl.className = "flex-1 text-sm"
    msgEl.textContent = message

    container.appendChild(iconDiv)
    container.appendChild(msgEl)
    document.body.insertBefore(container, document.body.firstChild)
  }
}
